import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  inventoryService,
  type InventoryItem,
  type InventorySupplier,
} from '@/services/inventoryService';
import { authService } from '@/services/authService';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  buildPriceHistoryByItemId,
  formatCentsSGD,
  toCents,
  type PriceHistoryEntry,
} from '@/lib/priceHistory';
import { format, parseISO } from 'date-fns';

/**
 * "Create PO directly" dialog — backlog item #15.
 *
 * Lets staff enter a PO without going through the room-request →
 * warehouse-plan path. Line items can be either a catalog inventory item
 * (FK to `inventory_items`) or a free-text description for items not yet
 * in the catalog or one-off walk-in vendor offers.
 *
 * Save creates the PO with status='draft' via
 * `inventoryService.createPurchaseOrderDraft`. "Save and send" is item #16
 * (PO PDF + email) and is intentionally out of scope here.
 */

const FREE_TEXT_OPTION = '__free_text__';

type ComboboxOption = {
  value: string;
  label: string;
  /** Extra text rendered after the label and included in search matching. */
  hint?: string;
};

/** Cap rendered rows so a 2,000+ item catalog stays snappy in the popover. */
const COMBOBOX_RENDER_LIMIT = 80;

interface SearchableComboboxProps {
  id?: string;
  value: string;
  options: ComboboxOption[];
  /** Always shown at the top of the list, regardless of the search query. */
  pinnedOption?: ComboboxOption;
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
  onSelect: (value: string) => void;
  triggerRef?: React.Ref<HTMLButtonElement>;
}

/**
 * Searchable select (shadcn Popover + Command combobox pattern). Filtering is
 * manual (`shouldFilter={false}`) so only the first matches render — the
 * supplier and catalog directories are far too large for plain Select menus.
 */
function SearchableCombobox({
  id,
  value,
  options,
  pinnedOption,
  placeholder,
  searchPlaceholder,
  disabled,
  onSelect,
  triggerRef,
}: SearchableComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        (option.hint ? option.hint.toLowerCase().includes(q) : false)
    );
  }, [options, query]);

  const visible = filtered.slice(0, COMBOBOX_RENDER_LIMIT);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    if (pinnedOption && pinnedOption.value === value) return pinnedOption.label;
    return options.find((option) => option.value === value)?.label ?? null;
  }, [value, options, pinnedOption]);

  const handlePick = (next: string) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="min-h-11 w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selectedLabel && 'text-slate-500')}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
          />
          <CommandList className="max-h-64">
            <CommandEmpty>No matches.</CommandEmpty>
            {pinnedOption ? (
              <CommandItem
                value={pinnedOption.value}
                onSelect={() => handlePick(pinnedOption.value)}
                className="min-h-[44px]"
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    value === pinnedOption.value ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {pinnedOption.label}
              </CommandItem>
            ) : null}
            {visible.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={() => handlePick(option.value)}
                className="min-h-[44px]"
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    value === option.value ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <span className="truncate">
                  {option.label}
                  {option.hint ? (
                    <span className="text-slate-400"> — {option.hint}</span>
                  ) : null}
                </span>
              </CommandItem>
            ))}
          </CommandList>
          <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
            {filtered.length} match{filtered.length === 1 ? '' : 'es'}
            {filtered.length > visible.length
              ? ` · showing first ${visible.length}, type to narrow`
              : ''}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type LineDraft = {
  // Stable React key — survives reorders / catalog selection changes.
  key: string;
  // null = free-text item, string = inventory_items.id.
  inventoryItemId: string | null;
  // Used when no catalog item is selected.
  description: string;
  qty: string;
  unitPrice: string;
  uom: string;
  /**
   * Set on lines prefilled from outside (e.g. the Suggested reorders board)
   * before the catalog directory has loaded — once items arrive, the line
   * gets its UOM / unit-price defaults applied exactly as if the user had
   * picked the item, then the flag clears.
   */
  needsCatalogDefaults?: boolean;
};

function makeKey() {
  return `line-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

function emptyLine(): LineDraft {
  return {
    key: makeKey(),
    inventoryItemId: null,
    description: '',
    qty: '1',
    unitPrice: '',
    uom: '',
  };
}

function parseNumber(raw: string): number {
  const v = Number(raw);
  return Number.isFinite(v) ? v : 0;
}

function formatCurrency(value: number): string {
  return value.toFixed(2);
}

export interface NewPurchaseOrderDraftPrefill {
  supplierId?: string;
  lines?: Array<{ inventoryItemId: string; qty?: number }>;
}

export interface NewPurchaseOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save with the newly created PO id. */
  onCreated?: (purchaseOrderId: string) => void;
  /**
   * Optional prefill applied each time the dialog opens — used by the
   * Suggested reorders board (backlog item #14) to land staff in the
   * existing direct-PO flow with the supplier + item already chosen.
   */
  initialDraft?: NewPurchaseOrderDraftPrefill | null;
}

export function NewPurchaseOrderDialog({
  open,
  onOpenChange,
  onCreated,
  initialDraft,
}: NewPurchaseOrderDialogProps) {
  const { toast } = useToast();
  const currentUser = authService.getCurrentUser();

  const [suppliers, setSuppliers] = useState<InventorySupplier[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  // Newest-first PO unit-cost history per item (backlog item #8) — powers the
  // "Last paid …" hint when a draft line's price differs from the last one.
  const [priceHistoryByItemId, setPriceHistoryByItemId] = useState<
    Map<string, PriceHistoryEntry[]>
  >(() => new Map());
  const [loadingDirectory, setLoadingDirectory] = useState(false);

  const [supplierId, setSupplierId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>(() => [emptyLine()]);
  const [submitting, setSubmitting] = useState(false);

  const supplierSelectRef = useRef<HTMLButtonElement | null>(null);

  // Reset form when the dialog opens, applying any caller prefill.
  useEffect(() => {
    if (!open) {
      return;
    }
    setSupplierId(initialDraft?.supplierId ?? '');
    setNotes('');
    setLines(
      initialDraft?.lines?.length
        ? initialDraft.lines.map((line) => ({
            ...emptyLine(),
            inventoryItemId: line.inventoryItemId,
            qty: String(line.qty != null && line.qty > 0 ? line.qty : 1),
            needsCatalogDefaults: true,
          }))
        : [emptyLine()]
    );
  }, [open, initialDraft]);

  // Prefilled lines arrive before the catalog directory finishes loading —
  // backfill their UOM / unit-price defaults once items are available.
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    setLines((prev) => {
      if (!prev.some((line) => line.needsCatalogDefaults)) {
        return prev;
      }
      return prev.map((line) => {
        if (!line.needsCatalogDefaults || !line.inventoryItemId) {
          return { ...line, needsCatalogDefaults: false };
        }
        const item = items.find((candidate) => candidate.id === line.inventoryItemId);
        return {
          ...line,
          uom: item?.order_unit ?? item?.unit ?? '',
          unitPrice: item?.cost_price != null ? String(item.cost_price) : '',
          needsCatalogDefaults: false,
        };
      });
    });
  }, [items]);

  // Pull suppliers + items + price history each time the dialog opens, so the
  // "last paid" hint reflects POs created since the dialog was last shown
  // (gating on empty arrays left the price history stale after the first open).
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoadingDirectory(true);
    inventoryService
      .getWorkflowSnapshot()
      .then((snapshot) => {
        if (cancelled) return;
        setSuppliers(snapshot.suppliers);
        setItems(snapshot.items.filter((item) => item.is_active));
        setPriceHistoryByItemId(
          buildPriceHistoryByItemId(snapshot.purchaseOrders, snapshot.purchaseOrderItems)
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to load suppliers/items for PO dialog:', error);
        toast({
          title: 'Could not load suppliers',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to fetch the catalog and supplier directory.',
          variant: 'destructive',
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingDirectory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, toast]);

  const sortedSuppliers = useMemo(
    () => [...suppliers].sort((a, b) => a.name.localeCompare(b.name)),
    [suppliers]
  );

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    [items]
  );

  const itemById = useMemo(() => {
    const map = new Map<string, InventoryItem>();
    items.forEach((item) => map.set(item.id, item));
    return map;
  }, [items]);

  const supplierOptions = useMemo<ComboboxOption[]>(
    () =>
      sortedSuppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
      })),
    [sortedSuppliers]
  );

  const itemOptions = useMemo<ComboboxOption[]>(
    () =>
      sortedItems.map((item) => ({
        value: item.id,
        label: item.name,
        hint: item.given_id ?? undefined,
      })),
    [sortedItems]
  );

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === supplierId) ?? null,
    [suppliers, supplierId]
  );

  // 92% of the clinic's suppliers have no email on file and POs go out via
  // WhatsApp — when a supplier has no phone AND no email, warn (non-blocking)
  // so staff know the PO can be saved but not sent anywhere.
  const supplierHasNoContact =
    selectedSupplier != null &&
    !selectedSupplier.handphone?.trim() &&
    !selectedSupplier.telephone?.trim() &&
    !selectedSupplier.email?.trim();

  const lineTotals = useMemo(
    () =>
      lines.map((line) => {
        const qty = parseNumber(line.qty);
        const unit = parseNumber(line.unitPrice);
        return qty > 0 && unit > 0 ? qty * unit : 0;
      }),
    [lines]
  );

  const grandTotal = useMemo(
    () => lineTotals.reduce((sum, lineTotal) => sum + lineTotal, 0),
    [lineTotals]
  );

  const handleLineChange = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
  };

  const handleItemPick = (key: string, value: string) => {
    if (value === FREE_TEXT_OPTION) {
      handleLineChange(key, { inventoryItemId: null });
      return;
    }
    const item = itemById.get(value);
    handleLineChange(key, {
      inventoryItemId: value,
      // Defaults from the catalog where available — user can still override.
      uom: item?.order_unit ?? item?.unit ?? '',
      unitPrice:
        item?.cost_price != null ? String(item.cost_price) : '',
    });
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (key: string) => {
    setLines((prev) => {
      if (prev.length <= 1) {
        return [emptyLine()];
      }
      return prev.filter((line) => line.key !== key);
    });
  };

  const validate = (): string | null => {
    if (!supplierId) {
      return 'Pick a supplier before saving.';
    }
    if (lines.length === 0) {
      return 'Add at least one line item.';
    }
    for (const line of lines) {
      const qty = parseNumber(line.qty);
      if (qty <= 0) {
        return 'Every line needs a quantity greater than zero.';
      }
      if (!line.inventoryItemId && !line.description.trim()) {
        return 'Each line needs either a catalog item or a free-text description.';
      }
    }
    return null;
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (submitting) return;

    if (!currentUser) {
      toast({
        title: 'No user session',
        description: 'Sign in again before creating a PO.',
        variant: 'destructive',
      });
      return;
    }

    const validationError = validate();
    if (validationError) {
      toast({
        title: 'Cannot save PO yet',
        description: validationError,
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      const created = await inventoryService.createPurchaseOrderDraft({
        supplierId,
        createdBy: currentUser.id,
        createdByEmail: currentUser.email,
        supplierMessage: notes.trim() || undefined,
        items: lines.map((line) => {
          const qty = parseNumber(line.qty);
          const unit = parseNumber(line.unitPrice);
          return {
            inventoryItemId: line.inventoryItemId,
            itemDescription: line.inventoryItemId
              ? null
              : line.description.trim(),
            qtyToOrder: qty,
            unitPrice: unit > 0 ? unit : null,
            uom: line.uom.trim() || null,
            // Keep unit_cost_snapshot aligned with the entered unit price so
            // downstream PO reporting still reads the value off the existing
            // column for now. Once item #16 ships, drop this in favour of
            // unit_price exclusively.
            unitCostSnapshot: unit > 0 ? unit : null,
          };
        }),
      });

      toast({
        title: 'PO draft created',
        description: `${created.po_number} saved as draft.`,
      });
      onOpenChange(false);
      onCreated?.(created.id);
    } catch (error: unknown) {
      console.error('Failed to create direct PO:', error);
      toast({
        title: 'PO draft creation failed',
        description:
          error instanceof Error
            ? error.message
            : 'The PO could not be saved. Check your entries and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    // Enter saves the form, unless the user is typing in the notes textarea
    // (where Enter should insert a newline as expected).
    if (event.key === 'Enter' && !event.shiftKey) {
      const target = event.target as HTMLElement | null;
      if (target && target.tagName === 'TEXTAREA') {
        return;
      }
      event.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New purchase order</DialogTitle>
          <DialogDescription>
            Create a PO directly — without going through a room request.
            Useful for admin supplies, urgent items not yet in the catalog, or
            walk-in vendor offers.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-5"
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
        >
          <div className="grid gap-2">
            <Label htmlFor="po-supplier">Supplier</Label>
            <SearchableCombobox
              id="po-supplier"
              triggerRef={supplierSelectRef}
              value={supplierId}
              options={supplierOptions}
              placeholder={
                loadingDirectory ? 'Loading suppliers…' : 'Pick a supplier'
              }
              searchPlaceholder="Search suppliers…"
              disabled={loadingDirectory}
              onSelect={setSupplierId}
            />
            {supplierHasNoContact ? (
              <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  This supplier has no phone or email on file - the PO can be
                  saved but cannot be sent. Add contact info in Catalog →
                  Suppliers.
                </span>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Line items</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={addLine}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add line
              </Button>
            </div>

            <ScrollArea className="max-h-[40vh] rounded-2xl border border-slate-200">
              <div className="divide-y divide-slate-200">
                {lines.map((line, index) => {
                  const lineTotal = lineTotals[index] ?? 0;
                  const isFreeText = line.inventoryItemId === null;
                  const selectedItem = line.inventoryItemId
                    ? itemById.get(line.inventoryItemId) ?? null
                    : null;
                  const packSize = selectedItem?.pack_size ?? 0;
                  const showPackHint =
                    selectedItem != null &&
                    packSize > 1 &&
                    !!selectedItem.order_unit &&
                    !!selectedItem.unit &&
                    selectedItem.order_unit !== selectedItem.unit;
                  const defaultedUnit =
                    selectedItem?.order_unit ?? selectedItem?.unit ?? null;
                  // Price-change hint (backlog item #8): compare the entered
                  // unit price against the last recorded PO cost in cents.
                  const lastPaid = line.inventoryItemId
                    ? priceHistoryByItemId.get(line.inventoryItemId)?.[0] ?? null
                    : null;
                  const enteredUnitPrice = parseNumber(line.unitPrice);
                  const priceDeltaCents =
                    lastPaid != null && enteredUnitPrice > 0
                      ? toCents(enteredUnitPrice) - lastPaid.unitCostCents
                      : null;
                  return (
                    <div
                      key={line.key}
                      className="grid gap-3 p-3 md:grid-cols-12"
                    >
                      <div className="md:col-span-5">
                        <Label
                          htmlFor={`line-item-${line.key}`}
                          className="text-xs uppercase tracking-wide text-slate-500"
                        >
                          Item
                        </Label>
                        <SearchableCombobox
                          id={`line-item-${line.key}`}
                          value={line.inventoryItemId ?? FREE_TEXT_OPTION}
                          options={itemOptions}
                          pinnedOption={{
                            value: FREE_TEXT_OPTION,
                            label: 'Other / not in catalog',
                          }}
                          placeholder="Pick an item"
                          searchPlaceholder="Search items…"
                          disabled={loadingDirectory}
                          onSelect={(value) => handleItemPick(line.key, value)}
                        />
                        {isFreeText ? (
                          <Input
                            className="mt-2"
                            placeholder="Describe the item"
                            value={line.description}
                            onChange={(event) =>
                              handleLineChange(line.key, {
                                description: event.target.value,
                              })
                            }
                          />
                        ) : null}
                      </div>

                      <div className="md:col-span-2">
                        <Label
                          htmlFor={`line-qty-${line.key}`}
                          className="text-xs uppercase tracking-wide text-slate-500"
                        >
                          Qty
                        </Label>
                        <Input
                          id={`line-qty-${line.key}`}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="any"
                          value={line.qty}
                          onChange={(event) =>
                            handleLineChange(line.key, {
                              qty: event.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label
                          htmlFor={`line-price-${line.key}`}
                          className="text-xs uppercase tracking-wide text-slate-500"
                        >
                          Unit price
                        </Label>
                        <Input
                          id={`line-price-${line.key}`}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="any"
                          value={line.unitPrice}
                          onChange={(event) =>
                            handleLineChange(line.key, {
                              unitPrice: event.target.value,
                            })
                          }
                        />
                        {priceDeltaCents != null && priceDeltaCents !== 0 ? (
                          <p
                            className={cn(
                              'mt-1 text-[11px] leading-4',
                              priceDeltaCents > 0 ? 'text-rose-600' : 'text-emerald-600'
                            )}
                          >
                            Last paid {formatCentsSGD(lastPaid!.unitCostCents)} on{' '}
                            {format(parseISO(lastPaid!.recordedAt), 'dd MMM yyyy')} (
                            {priceDeltaCents > 0 ? '+' : '-'}
                            {formatCentsSGD(priceDeltaCents)})
                          </p>
                        ) : null}
                      </div>

                      <div className="md:col-span-2">
                        <Label
                          htmlFor={`line-uom-${line.key}`}
                          className="text-xs uppercase tracking-wide text-slate-500"
                        >
                          UOM
                        </Label>
                        <Input
                          id={`line-uom-${line.key}`}
                          placeholder="e.g. BOX"
                          value={line.uom}
                          onChange={(event) =>
                            handleLineChange(line.key, {
                              uom: event.target.value,
                            })
                          }
                        />
                        {selectedItem && defaultedUnit ? (
                          <p className="mt-1 text-[11px] leading-4 text-slate-400">
                            Defaulted to {selectedItem.order_unit ? 'order unit' : 'unit'}{' '}
                            "{defaultedUnit}"
                            {showPackHint
                              ? ` · 1 ${selectedItem.order_unit} = ${packSize} ${selectedItem.unit}`
                              : ''}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex items-end justify-between gap-2 md:col-span-1">
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-slate-500">
                            Total
                          </p>
                          <p
                            className={cn(
                              'text-sm font-semibold',
                              lineTotal > 0
                                ? 'text-slate-900'
                                : 'text-slate-400'
                            )}
                          >
                            {formatCurrency(lineTotal)}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove line"
                          onClick={() => removeLine(line.key)}
                          className="text-slate-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">
                Order total
              </span>
              <span className="text-lg font-semibold text-slate-950">
                {formatCurrency(grandTotal)}
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="po-notes">Notes for supplier (optional)</Label>
            <Textarea
              id="po-notes"
              rows={3}
              placeholder="Delivery instructions, reference numbers, etc."
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="rounded-2xl">
              {submitting ? 'Saving…' : 'Save as draft'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewPurchaseOrderDialog;
